import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDollarSign,
  faMoneyBillWave,
  faMoneyBill,
  faChevronDown,
  faCheck,
  faExchangeAlt,
} from "@fortawesome/free-solid-svg-icons";
import { useCurrency } from "../../context/CurrencyContext";

// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
  success: "#16a34a",
};

// ============================================
// CURRENCY CONFIGURATION
// ============================================
const CURRENCIES = [
  {
    code: "SLL",
    symbol: "Le",
    label: "Sierra Leone Leone",
    icon: faMoneyBillWave,
    isPrimary: true,
    flag: "🇸🇱",
  },
  {
    code: "USD",
    symbol: "$",
    label: "US Dollar",
    icon: faDollarSign,
    isPrimary: false,
    flag: "🇺🇸",
  },
  {
    code: "MYR",
    symbol: "RM",
    label: "Malaysian Ringgit",
    icon: faMoneyBill,
    isPrimary: false,
    flag: "🇲🇾",
  },
];

// ============================================
// MAIN COMPONENT
// ============================================
const CurrencySelector = () => {
  const { currency, setCurrency, rates } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // ============================================
  // HELPERS
  // ============================================
  const getExchangeRateText = (currencyCode) => {
    if (currencyCode === "SLL") return "Primary Currency • Le";
    if (currencyCode === "USD")
      return `1 USD = ${rates.SLL?.toLocaleString() || "23,500"} SLL`;
    if (currencyCode === "MYR")
      return `1 MYR = ${Math.round(rates.SLL / rates.MYR || 4947).toLocaleString()} SLL`;
    return "";
  };

  const getFlagEmoji = (code) => {
    const curr = CURRENCIES.find((c) => c.code === code);
    return curr?.flag || "🌍";
  };

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentCurrency =
    CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="currency-selector-wrapper" ref={dropdownRef}>
      <button
        className="currency-selector-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Select currency"
        aria-expanded={isOpen}
      >
        <span className="currency-flag">
          {getFlagEmoji(currentCurrency.code)}
        </span>
        <FontAwesomeIcon icon={currentCurrency.icon} />
        <span className="currency-code-text">{currentCurrency.code}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`dropdown-arrow ${isOpen ? "open" : ""}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="currency-dropdown"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <div className="currency-dropdown-header">
              <FontAwesomeIcon icon={faExchangeAlt} />
              <span>Select Currency</span>
            </div>

            {CURRENCIES.map((curr) => {
              const isActive = currency === curr.code;
              const isPrimary = curr.isPrimary;

              return (
                <motion.button
                  key={curr.code}
                  className={`currency-option ${isActive ? "active" : ""} ${isPrimary ? "primary" : ""}`}
                  onClick={() => {
                    setCurrency(curr.code);
                    setIsOpen(false);
                  }}
                  whileHover={{ x: 4 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="currency-option-left">
                    <span className="currency-flag">{curr.flag}</span>
                    <FontAwesomeIcon icon={curr.icon} />
                    <div>
                      <div className="currency-code">
                        {curr.code}
                        {isActive && (
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="check-icon"
                          />
                        )}
                        {isPrimary && (
                          <span className="primary-badge">Primary</span>
                        )}
                      </div>
                      <div className="currency-name">{curr.label}</div>
                    </div>
                  </div>
                  <div className="currency-option-right">
                    <span className="currency-symbol">{curr.symbol}</span>
                    <span className="exchange-rate">
                      {getExchangeRateText(curr.code)}
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ============================================
          STYLES
          ============================================ */}
      <style>{`
        .currency-selector-wrapper {
          position: relative;
          display: inline-block;
        }

        .currency-selector-trigger {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.4rem 0.75rem;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 8px;
          color: #ffffff;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.813rem;
          font-weight: 500;
          font-family: "Inter", sans-serif;
        }

        .currency-selector-trigger:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(217, 163, 33, 0.3);
        }

        .currency-flag {
          font-size: 0.9rem;
        }

        .currency-code-text {
          font-weight: 600;
          font-size: 0.75rem;
        }

        .dropdown-arrow {
          font-size: 0.6rem;
          transition: transform 0.3s ease;
        }

        .dropdown-arrow.open {
          transform: rotate(180deg);
        }

        .currency-dropdown {
          position: absolute;
          top: calc(100% + 0.5rem);
          right: 0;
          min-width: 300px;
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
          padding: 0.5rem;
          z-index: 1000;
          border: 1px solid #e5e5e5;
        }

        .currency-dropdown-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem 0.75rem;
          font-size: 0.7rem;
          font-weight: 600;
          color: #5A5A5A;
          border-bottom: 1px solid #e5e5e5;
          margin-bottom: 0.25rem;
        }

        .currency-dropdown-header svg {
          color: #D9A321;
        }

        .currency-option {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          padding: 0.6rem 0.75rem;
          border: none;
          border-radius: 8px;
          background: transparent;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
          font-family: "Inter", sans-serif;
        }

        .currency-option:hover {
          background: #F8F8F8;
        }

        .currency-option.active {
          background: #e8f5e9;
          border-left: 3px solid #006B2E;
        }

        .currency-option.primary {
          border-bottom: 1px solid #e5e5e5;
          margin-bottom: 0.25rem;
          padding-bottom: 0.6rem;
        }

        .currency-option-left {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .currency-option-left .currency-flag {
          font-size: 1.1rem;
        }

        .currency-option-left svg {
          color: #006B2E;
          font-size: 1rem;
          width: 18px;
        }

        .currency-code {
          font-weight: 600;
          color: #1F1F1F;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
        }

        .check-icon {
          color: #16a34a;
          font-size: 0.7rem;
        }

        .primary-badge {
          font-size: 0.55rem;
          background: #D9A321;
          color: #ffffff;
          padding: 0.05rem 0.4rem;
          border-radius: 8px;
          font-weight: 600;
        }

        .currency-name {
          font-size: 0.7rem;
          color: #5A5A5A;
        }

        .currency-option-right {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 0.1rem;
        }

        .currency-symbol {
          font-weight: 600;
          color: #1F1F1F;
          font-size: 0.8rem;
        }

        .exchange-rate {
          font-size: 0.6rem;
          color: #9ca3af;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .currency-dropdown {
            min-width: 250px;
            right: -1rem;
          }

          .currency-option {
            padding: 0.5rem 0.6rem;
          }

          .currency-option-left .currency-flag {
            font-size: 0.9rem;
          }

          .currency-option-left svg {
            font-size: 0.875rem;
          }

          .currency-code {
            font-size: 0.813rem;
          }

          .currency-name {
            font-size: 0.65rem;
          }
        }

        @media (max-width: 480px) {
          .currency-selector-trigger {
            padding: 0.3rem 0.5rem;
            font-size: 0.7rem;
            gap: 0.3rem;
          }

          .currency-selector-trigger .currency-flag {
            font-size: 0.7rem;
          }

          .currency-dropdown {
            min-width: 200px;
            right: -0.5rem;
            padding: 0.4rem;
          }

          .currency-option {
            padding: 0.4rem 0.5rem;
            flex-wrap: wrap;
          }

          .currency-option-right {
            align-items: flex-start;
            margin-top: 0.1rem;
            width: 100%;
            padding-left: 2.5rem;
          }

          .exchange-rate {
            font-size: 0.55rem;
          }

          .currency-dropdown-header {
            font-size: 0.65rem;
            padding: 0.3rem 0.5rem 0.5rem;
          }
        }

        /* Scrolled Navbar Styles */
        .navbar.scrolled .currency-selector-trigger {
          color: #1F1F1F;
          background: rgba(0, 107, 46, 0.05);
          border-color: rgba(0, 107, 46, 0.1);
        }

        .navbar.scrolled .currency-selector-trigger:hover {
          background: rgba(0, 107, 46, 0.1);
        }

        /* Mobile Menu Styles */
        .nav-links.active .currency-selector-trigger {
          color: #1F1F1F;
          background: rgba(0, 107, 46, 0.05);
          border-color: rgba(0, 107, 46, 0.1);
          width: 100%;
          justify-content: center;
          padding: 0.75rem;
        }

        .nav-links.active .currency-selector-trigger:hover {
          background: rgba(0, 107, 46, 0.1);
        }
      `}</style>
    </div>
  );
};

export default CurrencySelector;
